import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "../Utils/Loader";
import { getAllTransferInventory } from "../../slices/Transfer/GetallTransferInventory/thunk";

const Icon = ({ d, size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d={d} />
  </svg>
);
const ArrowLeftIcon = () => <Icon d="M15 18l-6-6 6-6" />;
const ArrowRightIcon = () => <Icon d="M9 18l6-6-6-6" />;
const CloseIcon = () => <Icon d="M18 6 6 18M6 6l12 12" />;
const PackageIcon = () => (
  <Icon
    d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10"
    size={18}
  />
);

const TYPE_STYLES = {
  stockin: {
    bg: "#F0FDF4",
    color: "#15803D",
    border: "#BBF7D0",
    accentBar: "#22C55E",
  },
  stockout: {
    bg: "#FFF1F2",
    color: "#BE123C",
    border: "#FECDD3",
    accentBar: "#F43F5E",
  },
  transfer: {
    bg: "#EFF6FF",
    color: "#1D4ED8",
    border: "#BFDBFE",
    accentBar: "#3B82F6",
  },
  adjustment: {
    bg: "#FAF5FF",
    color: "#7E22CE",
    border: "#E9D5FF",
    accentBar: "#A855F7",
  },
};
const getTypeStyle = (type = "") =>
  TYPE_STYLES[type.toLowerCase()] ?? {
    bg: "#F3F4F6",
    color: "#374151",
    border: "#D1D5DB",
    accentBar: "#9CA3AF",
  };

  const getTransactionLabel = (type) => {
  switch (type?.toLowerCase()) {
    case "stockin":
      return "STOCK IN";
    case "stockout":
      return "STOCK OUT";
    case "transfer":
      return "TRANSFER";
    case "adjustment":
      return "ADJUSTMENT";
    default:
      return type?.toUpperCase() || "";
  }
};

function resolveQty(txn) {
  const type = (txn.transactiontype || "").toLowerCase();
  if (type === "stockin") {
    return {
      value: txn.stockinquantity,
      sign: "+",
      color: "#15803D",
      bg: "#F0FDF4",
    };
  }
  if (type === "stockout") {
    return {
      value: txn.stockoutquantity,
      sign: "-",
      color: "#BE123C",
      bg: "#FFF1F2",
    };
  }

  const v = txn.quantity ?? 0;
  if (v < 0)
    return { value: Math.abs(v), sign: "-", color: "#BE123C", bg: "#FFF1F2" };
  return { value: v, sign: "+", color: "#15803D", bg: "#F0FDF4" };
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return (
    d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) +
    " · " +
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
  );
};

const ROWS_OPTIONS = [5, 10, 20, 50];

//////
export default function InventoryHistory({ selectedItem, onClose }) {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const { transferInventoryList, loading } = useSelector(
    (state) => state.GetAllTransferInventory || {},
  );

  const transactions = transferInventoryList?.transactions ?? [];
  const total =
    transferInventoryList?.totalcount ??
    transferInventoryList?.totalCount ??
    transactions.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  // Fetch on page / perPage / item change
  useEffect(() => {
    if (selectedItem?.itemid) {
      dispatch(getAllTransferInventory(perPage, page, selectedItem.itemid));
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }
  }, [dispatch, selectedItem?.itemid, page, perPage]);

  // Reset page when item or perPage changes
  useEffect(() => {
    setPage(1);
  }, [selectedItem?.itemid]);
  const handlePerPage = (e) => {
    setPerPage(Number(e.target.value));
    setPage(1);
  };

  const goTo = (p) => {
    if (p >= 1 && p <= totalPages && p !== page) setPage(p);
  };

  // Ellipsis page numbers
  const pageNumbers = () => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const nums = [1];
    if (page > 3) nums.push("…");
    for (
      let i = Math.max(2, page - 1);
      i <= Math.min(totalPages - 1, page + 1);
      i++
    )
      nums.push(i);
    if (page < totalPages - 2) nums.push("…");
    nums.push(totalPages);
    return nums;
  };

  const firstItem = total === 0 ? 0 : (page - 1) * perPage + 1;
  const lastItem = Math.min(page * perPage, total);

  return (
    <div
      className="bg-white h-100 d-flex flex-column mt-2"
      style={{
        fontFamily: "Inter, sans-serif",
        borderLeft: "1px solid #E5E7EB",
      }}
    >
      {/* HEADER */}
      <div
        className="d-flex justify-content-between align-items-start p-3 border-bottom"
        style={{ flexShrink: 0 }}
      >
        <div className="d-flex gap-2">
          <div>
            <i
              class="ri-map-pin-line"
              style={{ fontSize: "18px", color: "#FD7545" }}
            ></i>
          </div>
          <div>
            <div
              className="fw-semibold mb-1"
              style={{
                fontSize: "18px",
                color: "#111827",
              }}
            >
              Locations
            </div>

            <div
              style={{
                color: "#6B7280",
                fontSize: "12px",
              }}
            >
              {selectedItem?.sku || "SKU"} · {selectedItem?.itemname}
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="btn btn-sm"
          style={{
            border: "none",
            background: "transparent",
          }}
        >
          <CloseIcon />
        </button>
      </div>

      <div ref={scrollRef} className="flex-grow-1 overflow-auto">
        {/* TOTAL STOCK */}
        <div className="ps-3 pt-3 pe-2 border-bottom">
          <div
            className="d-flex justify-content-between align-items-center px-4 py-2"
            style={{
              borderRadius: 10,
              background: "#FFF7ED",
              border: "1px solid #F2E5D6",
            }}
          >
            <div
              style={{
                fontSize: 14,
                color: "#6B7280",
              }}
            >
              Total Stock
            </div>

            <div
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#FD7545",
              }}
            >
              {selectedItem?.itemcount || 0}
            </div>
          </div>

          {/* DISTRIBUTION */}
          <div
            className="mt-3 mb-3"
            style={{
              fontSize: 10,
              color: "#9CA3AF",
              letterSpacing: 1.5,
              fontWeight: 700,
            }}
          >
            DISTRIBUTION BY LOCATION
          </div>

          {selectedItem?.location?.map((loc) => {
            const percentage =
              ((loc.locationquantity || 0) / (selectedItem?.itemcount || 1)) *
              100;

            return (
              <div key={loc.locationid} className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#374151",
                    }}
                  >
                    {loc.locationname}
                  </div>

                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    {loc.locationquantity} units
                  </div>
                </div>

                <div
                  style={{
                    height: 7,
                    background: "#E5E7EB",
                    borderRadius: 999,
                  }}
                >
                  <div
                    style={{
                      width: `${percentage}%`,
                      height: "100%",
                      borderRadius: 999,
                      background: "#FD7545",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* TRANSACTION HEADER */}
        <div className="ps-4 pt-4 pb-1 pe-2">
          <div className="d-flex gap-2 align-items-center mb-2">
            <div className="d-flex align-items-center">
              <i
                className="ri-history-line"
                style={{ color: "#FD7545", fontSize: "14px" }}
              ></i>
            </div>
            <div
              className="fw-semibold"
              style={{
                fontSize: "16px",
                color: "#111827",
              }}
            >
              Transaction History
            </div>
          </div>

          <div
            className="p-3 mb-3"
            style={{
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              background: "#FFF",
            }}
          >
            <div className="d-flex justify-content-between mb-2">
              <div
                style={{
                  fontSize: "12px",
                  color: "#9CA3AF",
                }}
              >
                Product
              </div>

              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                {selectedItem?.itemname}
              </div>
            </div>

            <div className="d-flex justify-content-between">
              <div
                style={{
                  fontSize: "12px",
                  color: "#9CA3AF",
                }}
              >
                SKU
              </div>

              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                }}
              >
                {selectedItem?.sku}
              </div>
            </div>
          </div>

          {/* TRANSACTIONS */}
          {loading ? (
            <Loader />
          ) : (
            transactions.map((txn) => {
              const type = txn.transactiontype?.toLowerCase();

              const styles = {
                adjustment: {
                  bg: "#F5F3FF",
                  border: "#DDD6FE",
                  color: "#7C3AED",
                },

                stockin: {
                  bg: "#ECFDF5",
                  border: "#BBF7D0",
                  color: "#16A34A",
                },

                stockout: {
                  bg: "#FEF2F2",
                  border: "#FECACA",
                  color: "#DC2626",
                },

                transfer: {
                  bg: "#EFF6FF",
                  border: "#BFDBFE",
                  color: "#2563EB",
                },
              }[type];

              const qty = resolveQty(txn);

              return (
                <div
                  key={txn.transactionid}
                  className="mb-2"
                  style={{
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: `1px solid ${styles.border}`,
                  }}
                >
                  {/* TOP SECTION */}
                  <div
                    className="px-3 py-2"
                    style={{
                      background: styles.bg,
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <span
                          style={{
                            background: `${styles.color}20`,
                            color: styles.color,
                            fontWeight: 700,
                            fontSize: 10,
                            padding: "4px 8px",
                            borderRadius: 4,
                            letterSpacing: 1,
                          }}
                        >
                            {getTransactionLabel(txn.transactiontype)}
                        </span>

                        <div
                          className="mt-2"
                          style={{
                            color: "#4B5563",
                            fontSize: 12,
                          }}
                        >
                          {new Date(txn.createdat).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </div>
                      </div>

                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: 700,
                          color: styles.color,
                        }}
                      >
                        {qty.sign}
                        {qty.value}
                      </div>
                    </div>
                  </div>

                  {/* BOTTOM SECTION */}
                  <div
                    className="px-3 py-2"
                    style={{
                      background: "#FFF",
                    }}
                  >
                    {txn.fromlocationname && (
                      <div className="d-flex gap-2 align-items-center">
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#9CA3AF",
                            letterSpacing: 1,
                          }}
                        >
                          FROM
                        </div>

                        <div
                          style={{
                            fontSize: 12,
                            color: "#374151",
                          }}
                        >
                          {txn.fromlocationname}
                        </div>
                      </div>
                    )}

                    {txn.tolocationname && (
                      <>
                        {txn.fromlocationname && (
                          <div
                            style={{
                              fontSize: 14,
                              color: "#9CA3AF",
                            }}
                          >
                            ↓
                          </div>
                        )}

                        <div className="d-flex gap-2 align-items-center">
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#9CA3AF",
                              letterSpacing: 1,
                            }}
                          >
                            TO
                          </div>

                          <div
                            style={{
                              fontSize: 12,
                              color: "#374151",
                            }}
                          >
                            {txn.tolocationname}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* PAGINATION */}
      {!loading && total > 0 && (
        <div
          className="border-top px-4 py-3 d-flex justify-content-between align-items-center"
          style={{
            flexShrink: 0,
            background: "#FFF",
          }}
        >
          <div
            style={{
              color: "#6B7280",
              fontSize: 14,
            }}
          >
            {firstItem}-{lastItem} of {total} transactions
          </div>

          <div className="d-flex align-items-center gap-3">
            <button
              disabled={page === 1}
              onClick={() => goTo(page - 1)}
              className="btn btn-sm"
              style={{
                border: "none",
                background: "transparent",
                boxShadow: "none",
              }}
            >
              <ArrowLeftIcon />
            </button>

            <strong>
              {page} / {totalPages}
            </strong>

            <button
              disabled={page === totalPages}
              onClick={() => goTo(page + 1)}
              className="btn btn-sm"
              style={{
                border: "none",
                background: "transparent",
                boxShadow: "none",
              }}
            >
              <ArrowRightIcon />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function btnStyle(active, disabled) {
  return {
    width: 28,
    height: 28,
    minWidth: 28,
    borderRadius: 6,
    border: active ? "1.5px solid #2563EB" : "1px solid #D1D5DB",
    background: active ? "#2563EB" : "#FFFFFF",
    color: active ? "#FFFFFF" : "#374151",
    fontSize: 12,
    fontWeight: active ? 700 : 500,
    opacity: disabled ? 0.35 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}
